import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Image,
  Text,
  Container,
  Flex,
  VStack,
  Stack,
  Center,
  Spacer,
  ModalOverlay,
  Modal,
  ModalContent,
  ModalBody,
  Link,
} from '@chakra-ui/react';
import { useAppSelector, useAppDispatch } from '../../app/hooks';

import { MyBookingScreen } from './myBookingScreen';
import { BtNavigate } from '../../components/BtNavigate';
import BtFailed from '../../components/BtFailed';
import BtLoading from '../../components/BtLoading';
import BtInput from '../../components/BtInput';
import BtButton from '../../components/BtButton';
import { images } from '../../support/imageSupport';
import BtGroup from '../../components/BtGroup';
import BtListView from '../../components/BtListView';
import { LANDING_SCREEN_PATH } from '../../support/routeConstants';
import { LandingScreen } from '../landing/landingScreen';
import BtHelmet from '../../components/BtHelmet';
import BtSpinner from '../../components/BtSpinner';
import { useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import { fetchCompanyInformation } from '../gat/gatSlice';
import i18n from 'i18next';
import { mapBackendLocaleToI18n } from '../../support/helpers';

export const MyBooking: React.FC = () => {
  const viewModel = useAppSelector((state) => state.myBooking.viewModel);
  const logoUrl = useSelector((state: RootState) => state.gatInformation.logoUrl);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [load, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [state, setState] = useState({
    bookingId: viewModel.state.bookingId,
  });

  const companyInfoStatus = useSelector((state: RootState) => state.gatInformation.status);
  const locale = useSelector((state: RootState) => state.gatInformation.locale);

  useEffect(() => {
    if (companyInfoStatus !== 'succeeded') return;

    i18n.changeLanguage(mapBackendLocaleToI18n(locale));

    if (MyBookingScreen.isNone(viewModel)) {
      dispatch(MyBookingScreen.start(viewModel, logoUrl));
    } else if (MyBookingScreen.isLoading(viewModel)) {
      (async () => {
        dispatch(await MyBookingScreen.load(viewModel, logoUrl));
      })();
    }
  }, [viewModel, companyInfoStatus]);

  useEffect(() => {
    dispatch(fetchCompanyInformation());
  }, [dispatch]);

  const handleNavigate = async (path: string) => {
    if (path.length === 0) {
      navigate(-1);
    } else {
      dispatch(await LandingScreen.reset(logoUrl));
      navigate(path);
    }
  };
  const handleOnChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const target = event.target;
    const value = target.value.toUpperCase();
    setState({ ...state, [target.name]: value });
    dispatch(await MyBookingScreen.uppdateState(viewModel, event, logoUrl));
  };

  const handleSearch = async () => {
    setLoading(true);
    setVisible(true);
    dispatch(await MyBookingScreen.search(viewModel, logoUrl));
    setLoading(false);
    setVisible(false);
  };

  const handleShowModel = async () => {
    dispatch(await MyBookingScreen.showModal(viewModel, logoUrl));
  };

  const handleCancel = async () => {
    setLoading(true);
    setVisible(false);
    dispatch(await MyBookingScreen.cancel(viewModel, logoUrl));
    setLoading(false);
  };

  const handleInterrupt = () => {
    MyBookingScreen.cancelFetch();
    setLoading(false);
  };

  const element = document.getElementById('ScrollToMe');
  if (element !== null && viewModel.state.scroll) {
    const rect = element.getBoundingClientRect();
    const offsetTop = rect.top + document.body.scrollTop;
    window.scrollTo(0, offsetTop);
  }

  if (MyBookingScreen.isLoading(viewModel)) {
    return <BtLoading {...viewModel.loading} />;
  } else if (MyBookingScreen.isReady(viewModel)) {
    return (
      <>
        <BtHelmet title={viewModel.metaTitle} />
        <BtSpinner
          {...viewModel.spinner}
          visible={visible}
          show={load}
          onClick={() => handleInterrupt()}
        />
        <Container padding={0} size='xl'>
          <BtNavigate {...viewModel.navigation} onClick={handleNavigate} />
          <Container padding={0} maxW='container.md'>
            <Flex pb={0}>
              <VStack w='full' h='full' p={10}>
                <Heading as='h1' variant='h1' pb={5}>
                  {viewModel.greeting}
                </Heading>
                <Heading pb={7} as='h4' variant='h4' textAlign={'center'}>
                  {viewModel.body}
                </Heading>
                <Flex w='full' pb={4}>
                  <Stack w='full' spacing={[2, 8]} direction={['column', 'row']}>
                    <BtInput
                      {...viewModel.bookingId}
                      value={state.bookingId}
                      onChange={handleOnChange}
                      type={'email'}
                    />
                  </Stack>
                </Flex>
                <BtButton
                  variant='primary'
                  size='lg'
                  w='full'
                  {...viewModel.search}
                  action={() => handleSearch()}
                />
              </VStack>
            </Flex>
          </Container>

          <Container padding={0} maxW='100%' bg='darkgray.100' height='100vh'>
            <Container padding={0} maxW='container.md'>
              <Flex pb={200}>
                <VStack w='full' h='full' p={10}>
                  <Flex w='full' pb={150} id='ScrollToMe'>
                    {viewModel.state.date !== '' && (
                      <Stack w='full' spacing={4} direction={['column', 'column']}>
                        <Text as='h4' fontSize='xl'>
                          {viewModel.bookingResult}
                        </Text>
                        <Box
                          w='100%'
                          p={4}
                          bg='black'
                          borderRadius='lg'
                          border='1px solid rgba(255, 212, 163, 0.2)'
                        >
                          <VStack spacing='5' w='100%'>
                            <Stack
                              w='full'
                              spacing={5}
                              direction={['column', 'column']}
                              p={5}
                              alignItems='start'
                            >
                              <Stack spacing={50} direction={['column', 'row']}>
                                <BtListView {...viewModel.date} />
                                <BtListView {...viewModel.time} />
                              </Stack>
                              <BtListView {...viewModel.registrationNumber} />
                            </Stack>
                          </VStack>
                          <Stack w='full' spacing={2} direction={['column', 'column']}>
                            <BtButton
                              variant='outline'
                              size='lg'
                              w='full'
                              {...viewModel.cancel}
                              action={() => handleShowModel()}
                            />
                          </Stack>
                        </Box>
                      </Stack>
                    )}
                  </Flex>

                  <Modal
                    isOpen={viewModel.state.showModal}
                    onClose={() => handleShowModel()}
                    isCentered
                    size='xl'
                  >
                    <ModalOverlay
                      bg='rgba(0, 0, 0, 0.6)'
                      backdropFilter='auto'
                      backdropBlur='2px'
                    />
                    <ModalContent bg='black' boxShadow='brand'>
                      <Box w='100%' p={50} bg='black' borderRadius='lg'>
                        <Stack>
                          <Text as='h3' fontSize='2xl' align='center'>
                            {viewModel.confirmTitle}
                          </Text>
                          <Spacer />

                          <ModalBody>
                            <Stack w='full' spacing={8} direction={['column', 'row']}>
                              <BtButton
                                variant='outline'
                                size='lg'
                                w='full'
                                {...viewModel.no}
                                action={() => handleShowModel()}
                              />
                              <BtButton
                                variant='primary'
                                size='lg'
                                w='full'
                                {...viewModel.yes}
                                action={() => handleCancel()}
                              />
                            </Stack>
                          </ModalBody>
                        </Stack>
                      </Box>
                    </ModalContent>
                  </Modal>
                </VStack>
              </Flex>
            </Container>
          </Container>
        </Container>
      </>
    );
  } else if (MyBookingScreen.isFinish(viewModel)) {
    return (
      <Container padding={0} size='xl'>
        <BtNavigate {...viewModel.navigation} onClick={handleNavigate} />
        <Container padding={0} maxW='container.md'>
          <Flex pb={200}>
            <VStack w='full' h='full' p={10}>
              <Box w='100%' p={4}>
                <VStack spacing='5' w='100%'>
                  <Stack
                    w='full'
                    spacing={5}
                    direction={['column', 'column']}
                    p={5}
                    alignItems='center'
                  >
                    <Image src={images(viewModel.image)} />
                    <Heading as='h1' variant='h1' textAlign='center'>
                      {viewModel.title}
                    </Heading>
                    <Text align='center'>{viewModel.body}</Text>

                    {viewModel.phone !== '' && (
                      <Stack spacing={5} direction={['row', 'row']} p={5} alignItems='center'>
                        <Image src={images(viewModel.imageSupport)} />
                        <Link href={`tel:+${viewModel.phone}`} color='brand.500'>
                          {viewModel.phone}
                        </Link>
                      </Stack>
                    )}
                  </Stack>
                </VStack>
                <Box p={8}>
                  <BtButton
                    variant='primary'
                    size='lg'
                    w='full'
                    {...viewModel.ok}
                    action={() => handleNavigate(LANDING_SCREEN_PATH)}
                  />
                </Box>
              </Box>
            </VStack>
          </Flex>
        </Container>
      </Container>
    );
  } else if (MyBookingScreen.isFailed(viewModel)) {
    return <BtFailed {...viewModel.error} />;
  } else {
    return <BtLoading {...viewModel.loading} />;
  }
};
